/**
 * y-xyflow — Yjs ↔ xyflow store binding.
 *
 * Works with both `@xyflow/react` (web) and
 * `@fredericrous/xyflow-react-native` (iOS/Android/native web) since
 * both expose the same zustand-based `{ nodes, edges }` store shape.
 * No direct import of either package is required here; the binding
 * operates on a structural interface.
 *
 * ## Usage
 *
 * ```ts
 * import * as Y from "yjs";
 * import { bindYjsToFlow } from "@website-builder/y-xyflow";
 * import { useStoreApi } from "@xyflow/react"; // or react-native
 *
 * const ydoc = new Y.Doc();
 * const store = useStoreApi(); // zustand StoreApi
 *
 * const binding = bindYjsToFlow(ydoc, store);
 * // …later, on unmount:
 * binding.destroy();
 * ```
 *
 * ## Architecture
 *
 * Two sync directions, each with a cycle-break mechanism:
 *
 * **Direction 1 (store → Yjs):**
 *   `store.subscribe` fires on every state change. If
 *   `suppressLocalWrite` is false, we diff `prev` vs `next` and
 *   apply the delta to the Y.Map inside a `ydoc.transact` tagged
 *   `LOCAL_ORIGIN`. The transact batches the update into a single
 *   Yjs operation, reducing network traffic.
 *
 * **Direction 2 (Yjs → store):**
 *   `yNodes.observe` / `yEdges.observe` fire on every Y.Map change.
 *   Remote-origin transactions (origin ≠ LOCAL_ORIGIN) reconstruct
 *   the nodes/edges arrays and call `store.setState`. We hold
 *   `suppressLocalWrite = true` across the synchronous setState to
 *   prevent the store subscription from re-writing the same data back
 *   into Yjs. Setting state is synchronous in zustand, so the flag
 *   is released before the next tick.
 *
 * ## Human-review notes (T3.2)
 *
 * The following design choices have subtle implications and should be
 * reviewed before promoting to production:
 *
 * 1. **Whole-object values** — each node/edge is stored as one opaque
 *    value in Y.Map. Two peers editing different *fields* of the same
 *    node concurrently will have the last writer win on the entire
 *    object, not per-field. This is acceptable for v1 (solo editing
 *    + background sync) and matches the S5 spike design. Granular
 *    per-field sync requires nested Y.Map per node (v2 work).
 *
 * 2. **Shallow equality gating** — `diffIntoMap` uses one-level
 *    structural equality to skip no-op Y.Map sets. This is correct
 *    for nodes/edges whose mutable fields are primitives or plain
 *    objects. If a node's `data` field contains a reference that
 *    mutates in place (without a new reference), the binding will
 *    miss the change. Ensure all state updates follow immutable
 *    update patterns (standard for xyflow).
 *
 * 3. **Initial sync priority** — if the Y.Doc already contains data
 *    when the binding is created, the Y.Doc wins (late-joiner
 *    semantics). If the Y.Doc is empty and the store has data, the
 *    store populates the doc. This is consistent with CRDT
 *    conventions but means that starting with a pre-populated store
 *    AND a non-empty Y.Doc will silently overwrite the store with the
 *    Y.Doc contents.
 *
 * 4. **No undo integration at this layer** — Y.UndoManager is
 *    wired separately in T3.4 using `trackedOrigins: {LOCAL_ORIGIN}`.
 *    Remote changes (origin ≠ LOCAL_ORIGIN) are intentionally
 *    excluded from undo history so that a remote peer's edits cannot
 *    be undone by the local user.
 */
import * as Y from "yjs";
import { type WithId, diffIntoMap, readMap, preserveVolatile } from "./diff.js";
import { LOCAL_ORIGIN, HYDRATE_ORIGIN } from "./origin.js";

export { LOCAL_ORIGIN, HYDRATE_ORIGIN } from "./origin.js";
export type { WithId } from "./diff.js";

/**
 * Minimal structural interface required from the zustand store.
 * Intentionally loose — matches both `@xyflow/react` and
 * `@fredericrous/xyflow-react-native` store APIs without importing
 * from either package.
 */
export interface FlowStoreState<
  N extends WithId = WithId,
  E extends WithId = WithId,
> {
  nodes: N[];
  edges: E[];
}

export interface FlowStore<
  N extends WithId = WithId,
  E extends WithId = WithId,
> {
  getState(): FlowStoreState<N, E>;
  setState(partial: Partial<FlowStoreState<N, E>>): void;
  subscribe(
    listener: (state: FlowStoreState<N, E>, prev: FlowStoreState<N, E>) => void,
  ): () => void;
}

/** Returned by `bindYjsToFlow`; call `destroy()` on unmount. */
export interface BindingHandle {
  /** Tear down all subscriptions. Safe to call multiple times. */
  destroy(): void;
}

export interface BindOptions<
  N extends WithId = WithId,
  E extends WithId = WithId,
> {
  /**
   * How to apply a full Yjs→store update (undo, or a remote peer). Defaults to
   * `store.setState({ nodes, edges })`.
   *
   * CONTROLLED consumers MUST override this to route into their own state
   * setters (e.g. React `useNodesState`'s `setNodes`/`setEdges`). Writing
   * xyflow's internal store directly does NOT stick: nodes render from an
   * internal `nodeLookup` that `setState({ nodes })` doesn't touch, and the
   * controlled `nodes` prop re-syncs from React state on the next render — so an
   * undone node move/add would silently reappear. (Edges happen to render from
   * the `edges` array, which is why edge-undo worked but node-undo didn't.)
   *
   * The follow-on store→Yjs echo is a structural no-op (the store already
   * matches the doc), so an async setter is safe here.
   */
  applyRemote?: (nodes: N[], edges: E[]) => void;
}

/**
 * Bind a Yjs document to a zustand flow store.
 *
 * @param ydoc  The shared Y.Doc (must be the same instance on all
 *              peers, or synced via a Yjs provider).
 * @param store The xyflow zustand store (`useStoreApi()` return value).
 * @returns     A handle with a `destroy()` method for cleanup.
 */
export function bindYjsToFlow<
  N extends WithId = WithId,
  E extends WithId = WithId,
>(
  ydoc: Y.Doc,
  store: FlowStore<N, E>,
  options: BindOptions<N, E> = {},
): BindingHandle {
  const yNodes = ydoc.getMap<unknown>("nodes");
  const yEdges = ydoc.getMap<unknown>("edges");

  const applyRemote =
    options.applyRemote ??
    ((nodes: N[], edges: E[]) => store.setState({ nodes, edges }));

  // Flag set for the synchronous duration of a Yjs→store setState
  // call. Prevents the store subscription from echoing the change
  // back into Yjs. Synchronous setState in zustand makes this safe —
  // see design note (2) in the module doc above.
  let suppressLocalWrite = false;
  let destroyed = false;

  // ── Direction 1: store → Yjs ──────────────────────────────────────
  const unsubStore = store.subscribe((state, prev) => {
    if (suppressLocalWrite || destroyed) return;
    // TEARDOWN GUARD — xyflow RESETS its internal store to empty when the
    // <ReactFlow> component unmounts, and that reset races this binding's own
    // destroy(): the subscription sees "everything → nothing" and would
    // mirror it into the doc as DELETE-every-node/edge — which a provider
    // then broadcasts to every other tab/window editing the same site (the
    // cross-window draft-switch wipe, 2026-07-19: switching drafts in window
    // B emptied window A's canvas AND its persisted doc). A transition to a
    // COMPLETELY empty store while the doc still has content is teardown
    // noise, never authoring — deleting the LAST page must write the doc
    // directly (see the consumer's delete path), because from here the two
    // are indistinguishable.
    if (
      state.nodes.length === 0 &&
      state.edges.length === 0 &&
      (prev.nodes.length > 0 || prev.edges.length > 0) &&
      (yNodes.size > 0 || yEdges.size > 0)
    ) {
      return;
    }
    ydoc.transact(() => {
      diffIntoMap(prev.nodes as ReadonlyArray<N>, state.nodes, yNodes);
      diffIntoMap(prev.edges as ReadonlyArray<E>, state.edges, yEdges);
    }, LOCAL_ORIGIN);
  });

  // ── Direction 2: Yjs → store ──────────────────────────────────────
  const onRemote = (_event: Y.YMapEvent<unknown>, txn: Y.Transaction) => {
    if (
      txn.origin === LOCAL_ORIGIN ||
      txn.origin === HYDRATE_ORIGIN ||
      destroyed
    )
      return;
    // Re-attach this client's volatile UI state (selection, live drag flag,
    // measured size) onto the remote-authored items — otherwise applying the
    // echo of a local move would clobber the dragged node's `selected` flag.
    const prev = store.getState();
    const nodes = preserveVolatile(
      readMap<N>(yNodes),
      prev.nodes as ReadonlyArray<N>,
    );
    const edges = preserveVolatile(
      readMap<E>(yEdges),
      prev.edges as ReadonlyArray<E>,
    );
    suppressLocalWrite = true;
    try {
      applyRemote(nodes, edges);
    } finally {
      suppressLocalWrite = false;
    }
  };

  yNodes.observe(onRemote);
  yEdges.observe(onRemote);

  // ── Initial sync ──────────────────────────────────────────────────
  // If the Y.Doc already has data (late joiner), it wins and populates
  // the store. If the Y.Doc is empty and the store has data (session
  // originator), push the store state into the doc.
  if (yNodes.size > 0 || yEdges.size > 0) {
    suppressLocalWrite = true;
    try {
      applyRemote(readMap<N>(yNodes), readMap<E>(yEdges));
    } finally {
      suppressLocalWrite = false;
    }
  } else {
    const { nodes, edges } = store.getState();
    if (nodes.length > 0 || edges.length > 0) {
      // HYDRATE_ORIGIN (not LOCAL_ORIGIN): the one-time seed of the empty doc is
      // not an undoable user action (consumers leave it out of trackedOrigins).
      ydoc.transact(() => {
        diffIntoMap([] as ReadonlyArray<N>, nodes, yNodes);
        diffIntoMap([] as ReadonlyArray<E>, edges, yEdges);
      }, HYDRATE_ORIGIN);
    }
  }

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      unsubStore();
      yNodes.unobserve(onRemote);
      yEdges.unobserve(onRemote);
    },
  };
}
